/**
 * Which record a route parameter names, and where an address goes once it names it by its
 * canonical key (COM-469, docs/id-free-addresses.md).
 *
 * The role applications are moving their addresses from database ids to slugs and ULID
 * `public_id`s, and the old addresses keep arriving: from bookmarks, QR codes, a stored
 * `redirectAfterAuth:<prefix>`, the server's `url.intended` and the legacy hosts' 301s. A
 * page finds its record in data it already loads, by its new key or by its old id, and
 * `useCanonicalRouteParams` (routeIdentifier.ts) puts the new key in the address.
 *
 * Nothing here imports React or React Router, so the unit project can load it in Node.
 * Pages import it through routeIdentifier.ts.
 */

/**
 * True for a value of digits only: an auto-increment id. A slug never is one
 * (`App\Support\UniqueContentSlug`), and a ULID never is in practice.
 */
export const isLegacyNumericKey = (value: unknown): boolean =>
  (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) ||
  (typeof value === 'string' && /^[0-9]+$/.test(value));

const field = (item: object, name: string): unknown => (item as Record<string, unknown>)[name];

/** An id's digits without leading zeros, so `007` names id 7 as the pages' `parseInt` read it. */
const idDigits = (value: string): string => value.replace(/^0+(?=[0-9])/, '');

/**
 * The item a route parameter names: the one whose `key` (its slug, unless another is
 * named) is the value, or else, for a value of digits only, the one whose `id` is that
 * number, leading zeros and all.
 */
export function findByRouteKey<Item extends object>(
  items: readonly Item[] | null | undefined,
  value: string | null | undefined,
  key = 'slug'
): Item | undefined {
  if (!items || value === null || value === undefined || value === '') return undefined;

  const byKey = items.find((item) => field(item, key) === value);
  if (byKey || !isLegacyNumericKey(value)) return byKey;

  const id = idDigits(value);
  return items.find((item) => {
    const itemId = field(item, 'id');
    return isLegacyNumericKey(itemId) && idDigits(String(itemId)) === id;
  });
}

/** What this module reads of a React Router match: its pattern, its params and the path it matched. */
export type RouteMatchShape = {
  params: Readonly<Record<string, string | undefined>>;
  pathnameBase: string;
  route: {path?: string};
};

/** Why an address was replaced: it held an old id, or another value than the record's key. */
export type CanonicalisedFrom = 'legacy-id' | 'mismatch';

/** A segment as React Router reads a param from it: decoded, with an encoded slash kept encoded. */
const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment).replace(/\//g, '%2F');
  } catch {
    return segment;
  }
};

/**
 * Where a param sits in the address: the index of its segment in `pathname.split('/')`,
 * and the value React Router read there. A param's segment is found from the pattern of
 * the deepest route that declares it, whose segments end where that route's match ends
 * (`pathnameBase`, which leaves out a splat). `null` when no matched route declares it, or
 * when an optional segment in the pattern means its place cannot be known.
 */
function paramSegment(matches: readonly RouteMatchShape[], name: string): {index: number; value?: string} | null {
  for (let at = matches.length - 1; at >= 0; at -= 1) {
    const {route, pathnameBase, params} = matches[at];
    const pattern = (route.path ?? '').split('/').filter((segment) => segment !== '' && segment !== '*');
    const position = pattern.indexOf(`:${name}`);
    if (position === -1) continue;
    if (pattern.some((segment) => segment.endsWith('?'))) return null;

    const index = pathnameBase.split('/').filter(Boolean).length - pattern.length + position;
    // `split('/')` puts an empty string before the leading slash.
    return index < 0 ? null : {index: index + 1, value: params[name]};
  }
  return null;
}

/**
 * The address a page's canonical values call for: `pathname` with the segment of each
 * param whose value differs swapped for the canonical one, every other segment and any
 * splat kept, and why. `null` when there is nothing to replace: a value is not known yet
 * (the record is loading, or was not found), every value is already the address's, or a
 * param cannot be placed in the address. All the params move in one replace, or none do.
 */
export function canonicalRoute(
  pathname: string,
  matches: readonly RouteMatchShape[],
  canonical: Readonly<Record<string, string | null | undefined>>
): {pathname: string; canonicalisedFrom: CanonicalisedFrom} | null {
  const wanted = Object.entries(canonical);
  if (wanted.length === 0 || wanted.some(([, value]) => value === null || value === undefined || value === '')) {
    return null;
  }

  const segments = pathname.split('/');
  let canonicalisedFrom: CanonicalisedFrom | null = null;
  for (const [name, value] of wanted as Array<[string, string]>) {
    const param = paramSegment(matches, name);
    if (param === null || param.value === undefined || decodeSegment(segments[param.index] ?? '') !== param.value) {
      return null;
    }
    const encodedValue = encodeURIComponent(value);
    if (param.value === decodeSegment(encodedValue)) continue;

    segments[param.index] = encodedValue;
    if (isLegacyNumericKey(param.value)) canonicalisedFrom = 'legacy-id';
    else canonicalisedFrom ??= 'mismatch';
  }

  return canonicalisedFrom === null ? null : {pathname: segments.join('/'), canonicalisedFrom};
}
